/** Fixed-capacity circular buffer for Float32 PCM audio samples. */
export class RingBuffer {
  private buffer: Float32Array;
  private writePos = 0;
  private filled = false;

  constructor(
    readonly capacity: number,
    readonly sampleRate: number
  ) {
    this.buffer = new Float32Array(capacity);
  }

  write(samples: Float32Array): void {
    const len = samples.length;

    if (len >= this.capacity) {
      this.buffer.set(samples.subarray(len - this.capacity));
      this.writePos = 0;
      this.filled = true;
      return;
    }

    const spaceToEnd = this.capacity - this.writePos;

    if (len <= spaceToEnd) {
      this.buffer.set(samples, this.writePos);
    } else {
      this.buffer.set(samples.subarray(0, spaceToEnd), this.writePos);
      this.buffer.set(samples.subarray(spaceToEnd), 0);
    }

    const newWritePos = (this.writePos + len) % this.capacity;
    if (newWritePos <= this.writePos && len > 0) {
      this.filled = true;
    }
    this.writePos = newWritePos;
  }

  readLastSamples(count: number): Float32Array {
    const available = this.availableSamples;
    const n = Math.min(count, available);
    if (n === 0) return new Float32Array(0);

    const result = new Float32Array(n);
    const startPos = (this.writePos - n + this.capacity) % this.capacity;

    if (startPos + n <= this.capacity) {
      result.set(this.buffer.subarray(startPos, startPos + n));
    } else {
      const firstChunk = this.capacity - startPos;
      result.set(this.buffer.subarray(startPos, this.capacity));
      result.set(this.buffer.subarray(0, n - firstChunk), firstChunk);
    }

    return result;
  }

  readAll(): Float32Array {
    return this.readLastSamples(this.availableSamples);
  }

  get availableSamples(): number {
    return this.filled ? this.capacity : this.writePos;
  }

  get durationMs(): number {
    return (this.availableSamples / this.sampleRate) * 1000;
  }

  clear(): void {
    this.writePos = 0;
    this.filled = false;
  }
}
